/**
 * CTR Gift Reveal Pre-Lander
 * Session countdown and sequential gift reveal (block order) on any grid cell.
 */

class CtrGiftRevealPreLander {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.countdownMinutes = parseInt(root.dataset.countdownMinutes, 10) || 15;
    this.persistSession = root.dataset.sessionStorage !== 'false';
    this.gifts = this.parseGifts();
    this.timerKey = `ctr-gift-reveal-timer-${this.sectionId}`;
    this.openedKey = `ctr-gift-reveal-opened-${this.sectionId}`;
    this.countdownEl = root.querySelector('[data-countdown]');
    this.boxes = Array.from(root.querySelectorAll('.ctr-gift-reveal__box'));
    this.timerInterval = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._sessionEndTime = null;
    this._openedBoxes = [];
    this.previewCards = Array.from(root.querySelectorAll('.ctr-gift-reveal__preview-card'));
    this.giftsRequired = parseInt(root.dataset.giftsRequired, 10) || 4;
    this.ctaPendingTemplate = root.dataset.ctaPendingTemplate || 'Pick {count} Gifts';
    this.ctaEl = root.querySelector('[data-cta]');
    this.ctaPendingEl = this.ctaEl?.querySelector('[data-cta-pending-text]');

    this.init();
  }

  parseGifts() {
    try {
      const script = this.root.querySelector('script[data-gifts-json]');
      if (!script) return [];
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('CtrGiftRevealPreLander: invalid gifts data', error);
      return [];
    }
  }

  init() {
    if (!this.gifts.length) return;

    this.preloadGiftImages();
    this.initCountdown();
    this.bindBoxes();
    this.restoreOpenedBoxes();
    this.updateCta();
  }

  preloadGiftImages() {
    const urls = new Set();

    const closedBoxUrl = this.root.dataset.closedBoxUrl;
    if (closedBoxUrl) urls.add(closedBoxUrl);

    this.gifts.forEach((gift) => {
      if (gift.preview_image) urls.add(gift.preview_image);
      const revealImage = gift.reveal_image || gift.preview_image;
      if (revealImage) urls.add(revealImage);
    });

    this.previewCards.forEach((card) => {
      const previewImg = card.querySelector('.ctr-gift-reveal__preview-image img');
      if (previewImg?.currentSrc || previewImg?.src) {
        urls.add(previewImg.currentSrc || previewImg.src);
      }
    });

    urls.forEach((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }

  destroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /* ---- Countdown ---- */

  getEndTime() {
    if (!this.persistSession) {
      if (!this._sessionEndTime) {
        this._sessionEndTime = Date.now() + this.countdownMinutes * 60 * 1000;
      }
      return this._sessionEndTime;
    }

    if (window.CtrSessionCountdown) {
      return window.CtrSessionCountdown.getEndTime(this.countdownMinutes, this.timerKey);
    }

    const stored = sessionStorage.getItem(this.timerKey);
    if (stored) {
      const end = parseInt(stored, 10);
      if (!Number.isNaN(end) && end > Date.now()) return end;
    }

    const end = Date.now() + this.countdownMinutes * 60 * 1000;
    sessionStorage.setItem(this.timerKey, String(end));
    return end;
  }

  formatCountdown(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  updateCountdown() {
    if (!this.countdownEl) return;

    const remaining = this.getEndTime() - Date.now();
    this.countdownEl.textContent = this.formatCountdown(remaining);
  }

  initCountdown() {
    if (!this.countdownEl) return;

    this.updateCountdown();
    this.timerInterval = setInterval(() => this.updateCountdown(), 1000);
  }

  /* ---- Box interaction ---- */

  bindBoxes() {
    this.boxes.forEach((box) => {
      box.addEventListener('click', () => this.openBox(box));
    });
  }

  getOpenedGiftCount() {
    return this.getOpenedBoxes().length;
  }

  isComplete() {
    return this.getOpenedGiftCount() >= this.giftsRequired;
  }

  populateBox(box, gift) {
    const prizeImage = box.querySelector('.ctr-gift-reveal__box-prize-image');
    if (!prizeImage || !gift) return;

    const revealImage = gift.reveal_image || gift.preview_image;
    if (revealImage) {
      prizeImage.innerHTML = `<img class="ctr-gift-reveal__box-closed-image" src="${revealImage}" alt="${this.escapeHtml(gift.label)}" width="86" height="86" loading="eager" decoding="async">`;
    }
  }

  revealBox(box, gift, giftIndex) {
    this.populateBox(box, gift);

    box.classList.add('ctr-gift-reveal__box--open');
    box.setAttribute('aria-expanded', 'true');
    box.disabled = true;

    this.revealPreviewCard(giftIndex);
  }

  openBox(box) {
    if (
      box.disabled ||
      box.classList.contains('ctr-gift-reveal__box--locked') ||
      box.classList.contains('ctr-gift-reveal__box--open')
    ) {
      return;
    }

    if (this.isComplete()) {
      this.lockRemainingBoxes();
      return;
    }

    const giftIndex = this.getOpenedGiftCount();
    const gift = this.gifts[giftIndex];
    if (!gift) return;

    const boxIndex = parseInt(box.dataset.boxIndex, 10);
    this.revealBox(box, gift, giftIndex);
    this.saveOpenedBox(boxIndex);
    this.updateCta();

    if (this.isComplete()) {
      this.lockRemainingBoxes();
    }
  }

  revealPreviewCard(giftIndex) {
    const card = this.previewCards[giftIndex];
    if (!card || card.classList.contains('ctr-gift-reveal__preview-card--revealed')) return;

    card.classList.add('ctr-gift-reveal__preview-card--revealed');

    const revealed = card.querySelector('.ctr-gift-reveal__preview-revealed');
    const label = card.querySelector('.ctr-gift-reveal__preview-label');

    if (revealed) revealed.removeAttribute('hidden');
    if (label) label.removeAttribute('aria-hidden');
  }

  lockRemainingBoxes() {
    this.boxes.forEach((box) => {
      if (box.classList.contains('ctr-gift-reveal__box--open')) return;

      box.disabled = true;
      box.classList.add('ctr-gift-reveal__box--locked');
      box.setAttribute('aria-disabled', 'true');
    });
  }

  updateCta() {
    if (!this.ctaEl) return;

    const ready = this.isComplete();
    const remaining = Math.max(0, this.giftsRequired - this.getOpenedGiftCount());

    this.ctaEl.classList.toggle('ctr-gift-reveal__cta--pending', !ready);
    this.ctaEl.classList.toggle('ctr-gift-reveal__cta--ready', ready);

    if (this.ctaPendingEl) {
      this.ctaPendingEl.textContent = this.ctaPendingTemplate.replace(
        /\{count\}/g,
        String(remaining)
      );
    }

    if (!ready) {
      this.ctaEl.setAttribute('aria-disabled', 'true');
      return;
    }

    if (this.ctaEl.tagName === 'A') {
      this.ctaEl.removeAttribute('aria-disabled');
      this.ctaEl.classList.remove('ctr-gift-reveal__cta--disabled');
    } else {
      this.ctaEl.classList.add('ctr-gift-reveal__cta--disabled');
      this.ctaEl.setAttribute('aria-disabled', 'true');
    }
  }

  getOpenedBoxes() {
    if (!this.persistSession) return this._openedBoxes;

    const stored = sessionStorage.getItem(this.openedKey);
    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  saveOpenedBox(boxIndex) {
    if (!this.persistSession) {
      this._openedBoxes.push(boxIndex);
      return;
    }

    const opened = this.getOpenedBoxes();
    opened.push(boxIndex);
    sessionStorage.setItem(this.openedKey, JSON.stringify(opened));
  }

  restoreOpenedBoxes() {
    const opened = this.getOpenedBoxes();

    opened.forEach((boxIndex, sequenceIndex) => {
      const box = this.boxes.find((el) => parseInt(el.dataset.boxIndex, 10) === boxIndex);
      const gift = this.gifts[sequenceIndex];
      if (box && gift) this.revealBox(box, gift, sequenceIndex);
    });

    if (this.isComplete()) {
      this.lockRemainingBoxes();
    }
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

const instances = new WeakMap();

function initGiftRevealSection(root) {
  if (!root || instances.has(root)) return;

  const instance = new CtrGiftRevealPreLander(root);
  instances.set(root, instance);
  root.giftRevealInstance = instance;
}

function destroyGiftRevealSection(root) {
  const instance = instances.get(root);
  if (instance) {
    instance.destroy();
    instances.delete(root);
    delete root.giftRevealInstance;
  }
}

function initAllGiftRevealSections() {
  document.querySelectorAll('.ctr-gift-reveal[data-section-id]').forEach(initGiftRevealSection);
}

document.addEventListener('DOMContentLoaded', initAllGiftRevealSections);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('.ctr-gift-reveal[data-section-id]');
    if (root) initGiftRevealSection(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('.ctr-gift-reveal[data-section-id]');
    if (root) destroyGiftRevealSection(root);
  });
}
